import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BomService, BomMaster, BomMaterial, BomTemplate } from '../shared/services/bom.service';
import { ApiService } from '../shared/services/api.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// Interfaces
interface Material {
  id: number;
  system_code?: string;
  code?: string;
  name: string;
  use_case?: string;
  category?: string;
  subcategory?: string;
  manufacturer_brand?: string;
  unit?: string;
  unit_cost?: number;
  total_cost?: number;
  location?: string;
  region?: string;
  specifications?: any;
  [key: string]: any;
}

interface MaterialCategory {
  id: number;
  name: string;
  description: string;
  parent_id: number;
  is_active: boolean;
}

// Remove local BomTemplate interface and use the one from BomService

@Component({
  selector: 'app-bom',
  templateUrl: './bom.component.html',
  styleUrls: ['./bom.component.css']
})
export class BomComponent implements OnInit, OnDestroy {
  bomForm!: FormGroup;
  boms: BomMaster[] = [];
  availableMaterials: Material[] = [];
  materialCategories: MaterialCategory[] = [];
  templates: BomTemplate[] = [];
  projects: any[] = [];
  
  loading = false;
  isSubmitting = false;
  selectedBom: BomMaster | null = null;
  showBomForm = false;
  showMaterialsPanel = false;
  showTemplatesPanel = false;
  quickAddSearch = new FormControl('');

  @ViewChild('bomComposer') bomComposer?: ElementRef<HTMLElement>;

  // Pagination
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  itemsPerPage = 10;

  // Search and filters
  searchTerm = '';
  statusFilter = '';
  projectFilter = '';
  materialCatalogSearch = '';

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private bomService: BomService,
    private snackBar: MatSnackBar
  ) {}

  get hasActiveFilters(): boolean {
    return !!(this.searchTerm || this.statusFilter || this.projectFilter);
  }

  ngOnInit(): void {
    this.initForm();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm() {
    this.bomForm = this.fb.group({
      project_id: ['', Validators.required],
      bom_code: [''],
      description: [''],
      version: [1, [Validators.min(1)]],
      status: ['draft'],
      materials: this.fb.array([])
    });
  }

  loadData() {
    this.loading = false;

    // Load BOMs
    this.loadBoms();

    // Load supporting data
    this.loadMaterials();
    this.loadProjects();
    // this.loadMaterialCategories();
    // this.loadTemplates();
  }

  loadBoms() {
    const params: any = {
      page: this.currentPage,
      limit: this.itemsPerPage,
      search: this.searchTerm,
      status: this.statusFilter
    };
    
    if (this.projectFilter) {
      params.project_id = parseInt(this.projectFilter);
    }

    this.bomService.getBoms(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.boms = response.data || [];
          if (response.pagination) {
            this.totalPages = response.pagination.totalPages;
            this.totalItems = response.pagination.totalCount;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading BOMs:', error);
          this.snackBar.open('Failed to load BOMs', 'Close', { duration: 3000 });
          this.loading = false;
        }
      });
  }

  loadMaterials() {
    this.apiService.getMaterialsMaster()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.availableMaterials = this.dedupeMaterials(res?.data || res || []);
          this.syncMaterialSearchLabels();
        },
        error: (error) => {
          console.error('Error loading materials:', error);
          this.availableMaterials = [];
        }
      });
  }

  loadMaterialCategories() {
    // For now, we'll use mock data since getMaterialCategories doesn't exist in BomService
    this.materialCategories = [
      { id: 1, name: 'Metals', description: 'Metal materials', parent_id: 0, is_active: true },
      { id: 2, name: 'Construction', description: 'Construction materials', parent_id: 0, is_active: true }
    ];
  }

  loadTemplates() {
    this.bomService.getBomTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.templates = response;
        },
        error: (error: any) => {
          console.error('Error loading templates:', error);
        }
      });
  }

  loadProjects() {
    // For now, we'll use mock data since getProjects doesn't exist in BomService
    this.apiService.getProjectCommonAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.projects = response?.data || response || [];
        },
      });
  }

  // FormArray getter
  get materials(): FormArray {
    return this.bomForm.get('materials') as FormArray;
  }

  addMaterial() {
    this.materials.push(this.createMaterialGroup());
  }

  addMaterialFromCatalog(material: Material): void {
    if (!material || typeof material !== 'object' || material.id == null) {
      return;
    }
    if (!this.showBomForm) {
      this.startNewBom();
    }
    const existingIndex = this.materials.controls.findIndex(control => control.get('material_id')?.value === material.id);
    if (existingIndex >= 0) {
      const qtyCtrl = this.materials.at(existingIndex).get('quantity');
      qtyCtrl?.setValue(Number(qtyCtrl.value || 0) + 1);
      this.snackBar.open(`${this.formatMaterialLabel(material)} quantity updated`, 'Close', { duration: 2000 });
    } else {
      this.materials.push(this.createMaterialGroup({
        material_id: material.id,
        quantity: 1,
        unit_rate: material.unit_cost ?? 0
      }));
    }
    setTimeout(() => this.quickAddSearch.setValue(''));
  }

  removeMaterial(index: number) {
    this.materials.removeAt(index);
  }

  startNewBom(): void {
    this.selectedBom = null;
    this.resetBomForm();
    this.showBomForm = true;
    this.scrollToComposer();
  }

  closeBomForm(): void {
    this.selectedBom = null;
    this.resetBomForm();
    this.showBomForm = false;
    this.closeSidePanels();
  }

  openMaterialsPanel(): void {
    this.showTemplatesPanel = false;
    this.showMaterialsPanel = true;
  }

  openTemplatesPanel(): void {
    this.showMaterialsPanel = false;
    this.showTemplatesPanel = true;
    if (!this.templates.length) {
      this.loadTemplates();
    }
  }

  closeSidePanels(): void {
    this.showMaterialsPanel = false;
    this.showTemplatesPanel = false;
  }

  private scrollToComposer(): void {
    setTimeout(() => this.bomComposer?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }

  createMaterialGroup(material?: Partial<BomMaterial> & { unit_cost?: number }): FormGroup {
    const selected = material?.material_id
      ? this.availableMaterials.find(m => m.id === material.material_id)
      : null;
    const fallbackLabel = !selected && (material?.material_code || material?.material_name)
      ? `${material.material_code || ''} - ${material.material_name || ''}`.replace(/^ - | - $/g, '').trim()
      : '';

    return this.fb.group({
      material_id: [material?.material_id || '', Validators.required],
      material_search: [selected || fallbackLabel],
      quantity: [material?.quantity ?? 1, [Validators.required, Validators.min(0.001)]],
      unit_rate: [material?.unit_rate ?? 0, [Validators.required, Validators.min(0)]],
      remarks: [material?.remarks || '']
    });
  }

  formatMaterialLabel(material: Material | string | null | undefined): string {
    if (!material) return '';
    if (typeof material === 'string') return material;
    const code = material.system_code || material.code || material.id;
    return code ? `${code} - ${material.name || ''}`.trim() : (material.name || '');
  }

  displayMaterialFn = (material: Material | string | null): string => {
    return this.formatMaterialLabel(material);
  };

  trackMaterial(_index: number, material: Material): number | string {
    return material?.id ?? material?.system_code ?? _index;
  }

  filterMaterials(value: string | Material | null | undefined): Material[] {
    if (!value || typeof value !== 'string') {
      return this.availableMaterials;
    }
    const query = value.trim().toLowerCase();
    if (!query) {
      return this.availableMaterials;
    }
    return this.availableMaterials.filter(material => {
      const code = String(material.system_code || material.code || '').toLowerCase();
      const name = String(material.name || '').toLowerCase();
      const category = String(material.category || material['category_name'] || '').toLowerCase();
      return code.includes(query) || name.includes(query) || category.includes(query);
    });
  }

  onMaterialSearchTyped(materialIndex: number, event: Event): void {
    const typed = ((event.target as HTMLInputElement)?.value || '').trim();
    const row = this.materials.at(materialIndex);
    const currentId = row.get('material_id')?.value;
    if (!currentId) {
      return;
    }
    const selected = this.availableMaterials.find(m => m.id === currentId);
    if (!selected || this.formatMaterialLabel(selected) !== typed) {
      row.patchValue({ material_id: '' }, { emitEvent: false });
    }
  }

  onMaterialSelect(materialIndex: number, material: Material) {
    if (!material || typeof material !== 'object') {
      return;
    }
    this.materials.at(materialIndex).patchValue({
      material_id: material.id,
      material_search: material,
      unit_rate: material.unit_cost ?? 0
    });
  }

  private syncMaterialSearchLabels(): void {
    this.materials.controls.forEach(control => {
      const materialId = control.get('material_id')?.value;
      if (!materialId) {
        return;
      }
      const selected = this.availableMaterials.find(m => m.id === materialId);
      if (selected) {
        control.patchValue({ material_search: selected }, { emitEvent: false });
      }
    });
  }

  private dedupeMaterials(materials: Material[]): Material[] {
    const seen = new Set<string>();
    return (materials || []).filter(material => {
      const key = String(material?.id ?? `${material?.system_code || material?.code}|${material?.name}`);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private serializeMaterials(): any[] {
    return this.bomForm.value.materials.map((material: any) => {
      const quantity = Number(material.quantity) || 0;
      const unitRate = Number(material.unit_rate) || 0;
      return {
        material_id: material.material_id,
        quantity,
        unit_rate: unitRate,
        remarks: material.remarks || '',
        total_amount: Number((quantity * unitRate).toFixed(2))
      };
    });
  }

  getCategoryName(categoryId: number): string {
    const category = this.materialCategories.find(c => c.id === categoryId);
    return category ? category.name : '';
  }

  calculateMaterialTotal(material: any): number {
    const quantity = Number(material.quantity) || 0;
    const unitRate = Number(material.unit_rate) || 0;
    return quantity * unitRate;
  }

  calculateBomTotals(): { subtotal: number; total: number } {
    const subtotal = this.materials.controls.reduce((sum, control) => {
      return sum + this.calculateMaterialTotal(control.value);
    }, 0);

    return {
      subtotal: Number(subtotal.toFixed(2)),
      total: Number(subtotal.toFixed(2))
    };
  }

  resetBomForm(): void {
    this.bomForm.reset({
      project_id: '',
      bom_code: '',
      description: '',
      version: 1,
      status: 'draft'
    });
    this.materials.clear();
    this.quickAddSearch.setValue('');
    this.isSubmitting = false;
    this.bomForm.markAsPristine();
    this.bomForm.markAsUntouched();
  }

  cancelEdit(): void {
    this.closeBomForm();
  }

  private validateBomForm(): boolean {
    this.bomForm.markAllAsTouched();
    if (this.bomForm.invalid) {
      this.snackBar.open('Please fill in all required fields', 'Close', { duration: 3000 });
      return false;
    }
    if (this.materials.length === 0) {
      this.snackBar.open('Add at least one material before saving', 'Close', { duration: 3000 });
      return false;
    }
    return true;
  }

  saveBom() {
    if (!this.validateBomForm()) {
      return;
    }

    this.isSubmitting = true;
    const formData = this.bomForm.value;
    const bomData = {
      project_id: formData.project_id,
      bom_code: formData.bom_code,
      description: formData.description,
      version: formData.version,
      status: formData.status,
      materials: this.serializeMaterials()
    };

    this.bomService.createBom(bomData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.snackBar.open('BOM created successfully', 'Close', { duration: 3000 });
          this.closeBomForm();
          this.loadBoms();
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Error creating BOM:', error);
          this.snackBar.open('Failed to create BOM', 'Close', { duration: 3000 });
          this.isSubmitting = false;
        }
      });
  }

  loadBomForEdit(bom: BomMaster) {
    this.selectedBom = bom;
    this.showBomForm = true;
    this.showMaterialsPanel = false;
    this.showTemplatesPanel = false;
    this.scrollToComposer();
    this.bomService.getBom(bom.bom_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.bomForm.patchValue({
            project_id: response.data.project_id,
            bom_code: response.data.bom_code,
            description: response.data.description,
            version: response.data.version,
            status: response.data.status
          });

          this.materials.clear();
          response.data.materials.forEach((material: BomMaterial) => {
            this.materials.push(this.createMaterialGroup({
              ...material,
              quantity: Number(material.quantity),
              unit_rate: Number(material.unit_rate)
            }));
          });
          this.bomForm.markAsPristine();
          this.bomForm.markAsUntouched();
        },
        error: (error) => {
          console.error('Error loading BOM:', error);
          this.snackBar.open('Failed to load BOM', 'Close', { duration: 3000 });
        }
      });
  }

  updateBom() {
    if (!this.selectedBom || !this.validateBomForm()) {
      return;
    }

    this.isSubmitting = true;
    const formData = this.bomForm.value;
    const bomData = {
      project_id: formData.project_id,
      bom_code: formData.bom_code,
      description: formData.description,
      version: formData.version,
      status: formData.status,
      materials: this.serializeMaterials()
    };

    this.bomService.updateBom(this.selectedBom.bom_id, bomData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.snackBar.open('BOM updated successfully', 'Close', { duration: 3000 });
          this.closeBomForm();
          this.loadBoms();
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Error updating BOM:', error);
          this.snackBar.open('Failed to update BOM', 'Close', { duration: 3000 });
          this.isSubmitting = false;
        }
      });
  }

  deleteBom(bom: BomMaster) {
    if (confirm(`Are you sure you want to delete BOM "${bom.bom_code}"?`)) {
      this.bomService.deleteBom(bom.bom_id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.snackBar.open('BOM deleted successfully', 'Close', { duration: 3000 });
            this.loadBoms();
          },
          error: (error) => {
            console.error('Error deleting BOM:', error);
            this.snackBar.open('Failed to delete BOM', 'Close', { duration: 3000 });
          }
        });
    }
  }

  loadTemplate(template: BomTemplate) {
    if (confirm(`Load template "${template.template_name}"? This will replace current materials.`)) {
      // Clear existing materials
      this.materials.clear();

      // Add template materials
      if (template.materials) {
        template.materials.forEach((material: BomMaterial) => {
          this.materials.push(this.createMaterialGroup(material));
        });
      }

      this.showBomForm = true;
      this.showTemplatesPanel = false;
      this.snackBar.open(`Template "${template.template_name}" loaded successfully`, 'Close', { duration: 3000 });
    }
  }

  exportBom(bom: BomMaster, format: 'json' | 'csv' = 'json') {
    this.bomService.exportBom(bom.bom_id, format)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Handle export response
          console.log('Export successful:', response);
          this.downloadBomPdf(response.data)
        },
        error: (error: any) => {
          console.error('Error exporting BOM:', error);
          this.snackBar.open('Failed to export BOM', 'Close', { duration: 3000 });
        }
      });
  }

  approveBom(bom: BomMaster) {
    const approvalData = {
      approver_id: 1, // Get from current user
      approval_level: 1,
      comments: 'Approved by system'
    };

    this.bomService.approveBom(bom.bom_id, approvalData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackBar.open('BOM approved successfully', 'Close', { duration: 3000 });
          this.loadBoms();
        },
        error: (error: any) => {
          console.error('Error approving BOM:', error);
          this.snackBar.open('Failed to approve BOM', 'Close', { duration: 3000 });
        }
      });
  }

  // Search and filter methods
  onSearch() {
    this.currentPage = 1;
    this.loadBoms();
  }

  onFilterChange() {
    this.currentPage = 1;
    this.loadBoms();
  }

  clearFilters() {
    this.searchTerm = '';
    this.statusFilter = '';
    this.projectFilter = '';
    this.onFilterChange();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadBoms();
  }

  // Utility methods
  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'draft': return 'status-draft';
      case 'pending': return 'status-pending';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      default: return 'status-default';
    }
  }

  getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'draft': return 'edit';
      case 'pending': return 'schedule';
      case 'approved': return 'check_circle';
      case 'rejected': return 'cancel';
      default: return 'help';
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString();
  }

  downloadBomPdf(bomData: any): void {
    const doc = new jsPDF('p', 'mm', 'a4');

    /* ---------------- Header ---------------- */
    doc.setFontSize(16);
    doc.text('Bill of Materials (BOM)', 14, 15);

    doc.setFontSize(10);
    doc.text(`Project Name: ${bomData.project_name}`, 14, 25);
    doc.text(`BOM Code: ${bomData.bom_code}`, 14, 31);
    doc.text(`Status: ${bomData.status}`, 14, 37);
    doc.text(`Version: ${bomData.version}`, 14, 43);
    doc.text(`Created By: ${bomData.creator_first_name}`, 14, 49);
    doc.text(
      `Created On: ${new Date(bomData.creation_date).toLocaleDateString()}`,
      14,
      55
    );

    /* ---------------- Materials Table ---------------- */
    const tableData = bomData.materials.map((m: any, index: number) => [
      index + 1,
      m.material_code,
      m.material_name,
      m.unit,
      m.quantity,
      m.unit_rate,
      m.total_amount,
      m.remarks
    ]);

    autoTable(doc, {
      startY: 65,
      head: [[
        '#',
        'Material Code',
        'Material Name',
        'Unit',
        'Qty',
        'Rate',
        'Amount',
        'Remarks'
      ]],
      body: tableData,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [63, 81, 181] }
    });

    /* ---------------- Total ---------------- */
    const total = bomData.materials.reduce(
      (sum: number, m: any) => sum + Number(m.total_amount),
      0
    );

    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.text(`Total Amount: ₹ ${total}`, 14, finalY);

    /* ---------------- Download ---------------- */
    doc.save(`BOM_${bomData.bom_code}.pdf`);
  }
}
